<template>
  <div>
    <div class="panel-heading order-heading-with-stats">
      <h4 class="panel-title">{{ $t('orders.processedOrdersTitle') }}</h4>

      <div class="order-heading-right">
        <div class="order-toolbar-buttons">
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="toggleFiltersVisible">
            <i class="material-icons">{{ showFilters ? 'expand_less' : 'expand_more' }}</i>
            <span v-if="showButtonLabels">{{ $t('orders.filtersToggleLabel') }}</span>
          </a>
          <a class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="reloadTable">
            <i class="material-icons">cached</i>
            <span v-if="showButtonLabels">{{ $t('datatable.reloadShortLabel') }}</span>
          </a>
          <a v-if="$_has_perm('approve_processed_order')" class="order-toolbar-btn" :class="{ 'order-toolbar-btn--icon-only': !showButtonLabels }" @click="approvingMultipleOrder()">
            <i class="material-icons" style="color:#006b8d">check_circle</i>
            <span v-if="showButtonLabels">{{ $t('orders.approveTooltip') }}</span>
          </a>
          <a class="order-toolbar-display-toggle" @click="$_toggle_toolbar_button_display()">
            <i class="material-icons">{{ showButtonLabels ? 'label_off' : 'label' }}</i>
          </a>
        </div>

        <div class="order-stats-row order-stats-row--compact">
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--blue"><i class="material-icons">list_alt</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('reports.totalOrders') }}</span>
              <span class="order-stat-value">{{ summaryTotalOrders }}</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--green"><i class="material-icons">attach_money</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('printLogs.totalAmountLabel') }}</span>
              <span class="order-stat-value">$ {{ summaryTotalAmount.toFixed(2) }}</span>
              <span v-if="orderIds.length > 0" class="order-stat-note">({{ orderIds.length }} selected)</span>
              <span v-else-if="!filterMade" class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--purple"><i class="material-icons">today</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.ordersTodayLabel') }}</span>
              <span class="order-stat-value">{{ ordersTodayCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
          <div class="order-stat-card">
            <div class="order-stat-icon order-stat-icon--orange"><i class="material-icons">payment</i></div>
            <div class="order-stat-text">
              <span class="order-stat-label">{{ $t('adminDashboard.pendingPaymentsLabel') }}</span>
              <span class="order-stat-value">{{ pendingPaymentsCount }}</span>
              <span class="order-stat-note">({{ $t('orders.currentPageScopeNote') }})</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="panel-body"
      style="display: block"
    >
      <div class="row">
        <preDatatableComponent
          ref="preDatatable"
          :removeAddButton="true"
          :removeWarehouseDeliverButton="true"
          :hideReloadButton="true"
          :collapsibleFilters="true"
          :filtersVisible="showFilters"
          :tableData="tableData"
          :permissions="{
            approve_all_permission: 'approve_processed_order'
          }"
          :loadApproveAll="loadApproveAll"
          :approvingMultipleOrder="approvingMultipleOrder"
          :resetGetResults="resetGetResults"
          :searchMe="searchMe"
          :enableCustomerSearchSuggestions="true"
          :pagination="pagination"
          :perPageFix="perPageFix"
          :filterTable="filterTable"
          :resetGetResults2="resetGetResults2"
          :columns="columns"
          :showHideColumn="showHideColumn"
          :storageKey="'cols_processedOrder_v3'"
          :defaultHideCol="defaultHideCol"
          :resultsVersion="resultsVersion"
          :filterMade="filterMade"
          :totalResult="totalResult"
          :warehouseFilterItems="warehouses"
          :warehouseFilterChange="handleWarehouseFilter"
          @branch-edit="branchEdit"
        ></preDatatableComponent>
        <div class="col-xs-12">
          <div
            v-if="sumOfSelectedOrders"
            class="row text-center"
          >
            <h5
              class="ma-0"
              style="font-weight: bold"
            >
              {{ $t('orders.sumOfSelectedOrdersLabel') }}: {{ $t('columns.purchaseCost') }}: {{ sumOfSelectedOrders.toFixed(3) }}, {{ $t('reports.totalCost') }}: {{ sumOfSelectedOrdersTotalCost.toFixed(3) }} ({{ (sumOfSelectedOrdersTotalCost - sumOfSelectedOrders).toFixed(2) }})
            </h5>
          </div>

          <div class="table-responsive oc-table-shell">
          <datatable
            v-if="!isMobile"
            :sortDir="tableData.sortDir"
            :columns="columns"
            :sortByColumn="sortByColumn"
            :searchOneCol="searchOneCol"
            :sortKey="sortKey"
            :sortOrders="sortOrders"
            :checkAll="checkAll"
            :unCheckAll="unCheckAll"
          >
            <tbody>
              <tr
                v-for="(r, index) in apiResults"
                :key="r.order_id"
                :style="orderIds.find(x => x == r.order_id) ? 'background-color: #FFF9C4' : '' "
              >
                <td>
                  <input
                    :id="'cb' + index"
                    type="checkbox"
                    :value="r.order_id"
                    v-model="orderIds"
                  />
                </td>
                <td class="oc-cell hover-container">
                  <div class="oc-order-number">#{{ r.theOrderID }}</div>
                  <div class="oc-order-sub"><view-order-users :orderData="r" /></div>
                </td>
                <td class="oc-cell ws-nowrap">
                  <customer-device-icon :orderSource="r.order_source" />
                  <img v-if="r.profile_image" :src="`${$_image_path()}${r.profile_image}`" class="customer-profile-image" />
                  <span class="customer-name-stack">
                    <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break: break-word; white-space: pre-line;">
                      {{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}
                    </span>
                    <span v-else>
                      {{ r.full_name }}
                    </span>
                    <span v-if="r.customer_id_fk" class="oc-customer-id">ID: {{ r.customer_id_fk }}</span>
                    <span v-if="r.phone_number" class="customer-phone-line">{{ r.phone_number }}</span>
                    <span v-if="r.phone_number_2" class="customer-phone-line">{{ r.phone_number_2 }}</span>
                  </span>
                  <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                </td>
                <td class="oc-cell">
                  <div class="oc-item-name">{{ r.product_name.substr(0, 35) + (r.product_name.length > 35 ? '...' : '') }}</div>
                  <div class="oc-item-sub" v-if="r.brand">{{ $t('columns.brand') }}: <strong>{{ r.brand }}</strong></div>
                  <a v-if="r.product_url" :href="r.product_url" target="_blank" rel="noopener noreferrer" class="oc-item-link">
                    <i class="material-icons">link</i> {{ $t('columns.url') }}
                  </a>
                </td>
                <td class="oc-cell oc-product-cell">
                  <img v-if="r.image" :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`" class="oc-thumb" @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"/>
                  <span v-else class="oc-thumb-placeholder">—</span>
                </td>
                <td class="oc-cell">
                  <span class="oc-status-chip" style="background:#FFC85C; color:#6B4C00;">{{ $_status_label(r.status) }}</span>
                  <span
                    v-if="r.is_ship_only == '1'"
                    class="oc-status-chip rtl-ml-2"
                    style="background-color:#FCE4EC; color:#C2185B;"
                  >{{ $t('columns.shipOnly') }}</span>
                  <div class="oc-location-line" v-if="r.country">
                    <i class="material-icons oc-location-icon">place</i>{{ r.country }}<span v-if="r.transportation_type_obj"> · {{ r.transportation_type_obj.name.replace('Transportation', '') }}</span>
                  </div>
                </td>
                <td class="combined-cell-td oc-cell">
                  <div class="combined-cell">
                    <div><span class="combined-cell-label">{{ $t('columns.price') }}:</span> {{ r.item_price ? '$' + r.item_price : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.shipping') }}:</span> {{ r.internal_shipping_handling ? '$' + r.internal_shipping_handling : '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.total') }}:</span> {{ r.total_amount ? '$' + r.total_amount : '—' }}</div>
                    <div class="combined-cell-divider"></div>
                    <div><span class="combined-cell-label">{{ $t('columns.qty') }}:</span> {{ r.quantity || '—' }}</div>
                    <div><span class="combined-cell-label">{{ $t('columns.size') }}:</span> {{ r.size || '—' }}</div>
                  </div>
                </td>
                <td class="oc-cell hover-container" style="min-width: 150px">
                  <view-order-dates :orderData="r" stack-time />
                  <span
                    v-if="r.payment_status"
                    class="oc-payment-chip"
                    :style="{ background: paymentStatusColor(r.payment_status, r.invoice_edit_count).bg, color: paymentStatusColor(r.payment_status, r.invoice_edit_count).color }"
                  >{{ paymentStatusLabel(r.payment_status) }}</span>
                </td>
                <td class="oc-cell oc-actions-cell">
                  <div class="oc-actions-grid">
                    <a
                      v-if="$_has_perm('view_order_detail')"
                      class="oc-action-btn"
                      :tooltip="$t('orders.viewDetails')"
                      @click="initDetail('modal_showApp', r.order_id)"
                    >
                      <i class="material-icons" style="color:#01579b">aspect_ratio</i>
                    </a>
                    <a
                      v-if="$_has_perm('approve_processed_order')"
                      class="oc-action-btn"
                      @click="approvingOrder(r)"
                      :tooltip="$t('orders.approveThisOrderTooltip')"
                    ><i class="material-icons" style="color:#43a047">check_circle</i></a>
                    <a
                      v-if="$_has_perm('cancel_processed_order')"
                      class="oc-action-btn"
                      @click="openCancelingOrderModal(r.order_id)"
                      :tooltip="$t('orders.cancelThisOrder')"
                    ><i class="material-icons" style="color:#c62828">cancel</i></a>
                    <a
                      v-if="$_has_perm('return_processed_order_to_new_order')"
                      class="oc-action-btn"
                      @click="returnOrderToNewOrder(r.order_id)"
                      :tooltip="r.is_ship_only == '1' ? $t('orders.returnToShipOnlyTooltip') : $t('orders.returnToNewOrderTooltip')"
                    ><i class="material-icons" style="color:#eb8545">settings_backup_restore</i></a>
                  </div>
                </td>
              </tr>
              <tr id="no-result-row">
                <td
                  :colspan="columns.length"
                  class="text-center"
                >
                  <v-alert
                    type="error"
                    :value="alertValue"
                  > {{ $t('common.noResultsFound') }} </v-alert>
                </td>
              </tr>
            </tbody>
          </datatable>
          </div>

          <div v-if="isMobile" class="moc-container">
            <div
              v-for="(r, index) in apiResults"
              :key="r.order_id"
              class="moc-card"
              :style="orderIds.find(x => x == r.order_id) ? 'border-left: 3px solid #FBC02D;' : ''"
            >
              <!-- Card header: checkbox + OrderID + status -->
              <div class="moc-header">
                <input
                  :id="'cb' + index"
                  type="checkbox"
                  :value="r.order_id"
                  v-model="orderIds"
                />
                <span class="moc-item-code">{{ r.theOrderID }}</span>
                <span class="moc-status-badge" style="background:#FFC85C; color:#6B4C00;">{{ $_status_label(r.status) }}</span>
              </div>
              <div class="moc-body">
                <!-- User -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('common.user') }}</span>
                  <span class="moc-value"><view-order-users :orderData="r" /></span>
                </div>

                <!-- Customer -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.customer') }}</span>
                  <span class="moc-value">
                    <customer-device-icon :orderSource="r.order_source" />
                    <img v-if="r.profile_image" :src="r.profile_image && r.profile_image.startsWith('http') ? r.profile_image : `${$_image_path()}${r.profile_image}`" class="customer-profile-image" />
                    <span v-if="r.full_name && r.full_name.trim().split(/\s+/).length > 3" style="word-break:break-word; white-space:pre-line;">
                      {{ r.full_name.replace(/((?:\S+\s+){3})/g, '$1\n') }}
                    </span>
                    <span v-else>{{ r.full_name }}</span>
                    <open-whatsapp-and-viber :phoneNumber="r.phone_number" />
                  </span>
                </div>

                <!-- Item Name -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.itemName') }}</span>
                  <span class="moc-value">{{ r.product_name.substr(0, 60) + (r.product_name.length > 60 ? '...' : '') }}</span>
                </div>

                <!-- Brand -->
                <div class="moc-row" v-if="r.brand">
                  <span class="moc-label">{{ $t('columns.brand') }}</span>
                  <span class="moc-value">{{ r.brand }}</span>
                </div>

                <!-- URL -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.url') }}</span>
                  <span class="moc-value">
                    <a v-if="r.product_url" :href="r.product_url" target="_blank" rel="noopener noreferrer" style="color:#01579B;">
                      <i class="material-icons" style="font-size:20px; vertical-align:middle;">link</i>
                    </a>
                    <span v-else style="color:#bbb;">—</span>
                  </span>
                </div>

                <!-- Image -->
                <div class="moc-row" v-if="r.image">
                  <span class="moc-label">{{ $t('columns.image') }}</span>
                  <span class="moc-value">
                    <img
                      :src="r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`"
                      style="width:80px; height:80px; object-fit:cover; border-radius:4px; cursor:pointer;"
                      @click="lightboxImage = r.image.startsWith('http') ? r.image : `${$_image_path()}${r.image}`; lightboxOpen = true"
                    />
                  </span>
                </div>

                <!-- Warehouse -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('columns.warehouse') }}</span>
                  <span class="moc-value">{{ r.country || '—' }}</span>
                </div>

                <!-- Price (incl. int. ship.) | Total — Shipping hidden -->
                <div class="moc-row moc-row--2col">
                  <div>
                    <span class="moc-label">{{ $t('columns.price') }}</span>
                    <span class="moc-value">{{ formatPriceInclIntShipping(r) }}</span>
                  </div>
                  <div>
                    <span class="moc-label">{{ $t('columns.total') }}</span>
                    <span class="moc-value">{{ r.total_amount ? '$' + r.total_amount : '—' }}</span>
                  </div>
                </div>

                <!-- Size -->
                <div class="moc-row" v-if="r.size">
                  <span class="moc-label">{{ $t('columns.size') }}</span>
                  <span class="moc-value">{{ r.size }}</span>
                </div>

                <!-- Ship Only -->
                <div class="moc-row" v-if="r.is_ship_only == '1'">
                  <span class="moc-label">{{ $t('columns.shipOnly') }}</span>
                  <span class="moc-value" style="color:#C2185B; font-weight:500;">{{ $t('common.yes') }}</span>
                </div>

                <!-- Date -->
                <div class="moc-row">
                  <span class="moc-label">{{ $t('common.date') }}</span>
                  <span class="moc-value"><view-order-dates :orderData="r" /></span>
                </div>
              </div>

              <!-- Card actions -->
              <div class="moc-actions">
                <a
                  v-if="$_has_perm('view_order_detail')"
                  style="color: #01579b" class="rtl-pr-3"
                  :tooltip="$t('orders.viewDetails')"
                  @click="initDetail('modal_showApp', r.order_id)"
                >
                  <i class="material-icons">aspect_ratio</i>
                </a>
                <!-- hidden: Process Order gear icon (v-if="false", kept for reversibility) -->
                <a
                  v-if="false"
                  style="color: #424242" class="rtl-pr-3"
                  :tooltip="$t('orders.processOrderTooltip')"
                  @click="openProcessOrderModal(r)"
                >
                  <i class="material-icons">settings</i>
                </a>
                <a
                  v-if="$_has_perm('approve_processed_order')"
                  @click="approvingOrder(r)"
                  style="color: #43a047" class="rtl-pr-3"
                  :tooltip="$t('orders.approveThisOrderTooltip')"
                ><i class="material-icons">check_circle</i></a>
                <a
                  v-if="$_has_perm('return_processed_order_to_new_order')"
                  @click="returnOrderToNewOrder(r.order_id)"
                  style="color: #eb8545" class="rtl-pr-3"
                  :tooltip="r.is_ship_only == '1' ? $t('orders.returnToShipOnlyTooltip') : $t('orders.returnToNewOrderTooltip')"
                ><i class="material-icons">settings_backup_restore</i></a>
                <a
                  v-if="$_has_perm('cancel_processed_order')"
                  @click="openCancelingOrderModal(r.order_id)"
                  style="color: #c62828" class="rtl-pr-3"
                  :tooltip="$t('orders.cancelThisOrder')"
                ><i class="material-icons">cancel</i></a>
              </div>
            </div>
            <v-alert type="error" :value="alertValue"> {{ $t('common.noResultsFound') }} </v-alert>
          </div>

          <!-- Lightbox dialog -->
          <v-dialog v-model="lightboxOpen" max-width="600px">
            <v-card>
              <v-card-text style="padding:8px; background:#000; text-align:center;">
                <img v-if="lightboxImage" :src="lightboxImage" style="max-width:100%; max-height:80vh;" />
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn flat @click="lightboxOpen = false">{{ $t('common.close') }}</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <view-order-details
            :detialData="detialData"
            :closeModal="closeModal"
          ></view-order-details>

          <process-the-order
            v-if="currentProcessOrder"
            ref="processComp"
            :processOrder="processOrder"
            :r="currentProcessOrder"
            :checkAlert="checkAlert"
            :updateData="updateData"
            :calculateTotalAmount="calculateTotalAmount"
            :loading="loading"
            :getResults="getResults"
            :clearProcessOrderData="clearProcessOrderData"
            :theQuantity="theQuantity"
            :itemTypes="itemTypes"
            :warehouses="warehouses"
          ></process-the-order>

          <admin-cancel-order
            :closeCancelingOrderModal="closeCancelingOrderModal"
            :cancelOrderModal="cancelOrderModal"
            :cancelAlert="cancelAlert"
            :cancelData="cancelData"
            :cancelingOrder="cancelingOrder"
            :reasons="reasons"
          ></admin-cancel-order>

          <view-log-details
            :detialData="detialData"
            :closeModal="closeModal"
            :companyData="companyData"
            :exRate="exRate"
          ></view-log-details>

          <v-dialog
            v-model="approvingDialog"
            :overlay="false"
            max-width="500px"
            transition="dialog-transition"
          >
            <v-card>
              <v-card-title
                primary-title
                class="pb-2 pt-3"
              >
                <h3 class="my-0">{{ $t('common.areYouSure') }}</h3>
              </v-card-title>
              <v-card-text>
                <v-layout
                  row
                  wrap
                  class="mx-2"
                >
                  <v-flex xs12>
                    <h6>{{ $t('columns.customer') }}: {{ orderData.full_name }}</h6>
                  </v-flex>
                  <v-flex xs12>
                    <h6>{{ $t('columns.balance') }}: ${{ orderData.credit }}</h6>
                  </v-flex>
                  <v-flex xs12>
                    <h6>{{ $t('shipOnly.orderPriceLabel') }}: ${{ orderData.total_amount }}</h6>
                  </v-flex>

                  <!-- <v-flex xs12>
                    <branch-select
                      label="Approve branch *"
                      v-model="orderData.branch_id_fk"
                    />
                  </v-flex> -->

                  <!-- <v-flex xs12>
                    <branch-select
                      label="Deliver branch *"
                      v-model="orderData.deliver_branch_id_fk"
                    />
                  </v-flex> -->

                  <v-flex xs12>
                    <v-radio-group v-model="approveValue">
                      <v-radio
                        :label="$t('shipOnly.willPayOption')"
                        value="1"
                      />
                      <v-radio
                        :label="$t('shipOnly.loanOption')"
                        value="2"
                        :disabled="orderData.credit >= orderData.total_amount"
                      />
                    </v-radio-group>
                  </v-flex>

                  <v-flex
                    xs12
                    v-show="approveValue === '1'"
                  >
                    <a
                      href="/secret?component=credit"
                      target="blank"
                      style="font-size: 14px; text-decoration: underline"
                    >
                      {{ $t('shipOnly.goToCustomerCreditLink') }}
                    </a>
                  </v-flex>
                </v-layout>
              </v-card-text>

              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  style="height: auto"
                  @click="approvingDialog = false"
                  outline
                  color="normal"
                >{{ $t('common.close') }}</v-btn>
                <v-btn
                  style="height: auto" class="rtl-mr-1-5pct"
                  outline
                  color="success"
                  @click="approveOrder"
                  :loading="loadSingleApprove"
                >ok</v-btn>
              </v-card-actions>
            </v-card>
          </v-dialog>

          <div class="row">
            <div class="col-xs-12 text-center oc-pagination">
              <paginate
                :page-count="pagination.lastPage"
                :page-range="2"
                :changePage="changePage"
                :container-class="'pagination'"
                :pagination="pagination"
                :page-class="'page-item'"
              >
              </paginate>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import PaginationVue from './Pagination.vue'
import preDatatableComponent from './preDatatableComponent.vue'
import DatatableVue from './Datatable.vue'
import viewOrderDetails from './viewOrderDetails.vue'
import axios from 'axios'
import swal from 'sweetalert'
import adminCancelOrder from './adminCancelOrder'
import processTheOrder from './processTheOrder.vue'
import viewLogDetails from './logs/viewLogDetails.vue'
import statusHelpers from '../mixins/statusHelpers'
import toolbarButtonDisplay from '../mixins/toolbarButtonDisplay'

export default {
  mixins: [statusHelpers, toolbarButtonDisplay],
  components: {
    preDatatableComponent: preDatatableComponent,
    datatable: DatatableVue,
    paginate: PaginationVue,
    'view-order-details': viewOrderDetails,
    'admin-cancel-order': adminCancelOrder,
    'process-the-order': processTheOrder,
    'view-log-details': viewLogDetails
  },
  mounted() {
    this.$_fetch_statuses()
    this.perPageFix()
    this.getReasons()
    this.getWarehouses()
    this.getItemType()

    if (screen.width <= 600) {
      this.isMobile = true
    } else {
      this.isMobile = false
    }
  },
  $_veeValidate: {
    validator: 'new'
  },
  data() {
    let sortOrders = {}
    // Grouped columns (redesign, mirrors allOrder.vue's pattern) -- was a flat 18-column
    // list; each grouped cell stacks several related fields (the processed-by user folds
    // into Order Info, ship-only into Status & Location). Per-column hide now only makes
    // sense at the group level, so defaultHideCol is emptied and storageKey bumped
    // (v2 -> v3) so stale hidden-column selections don't misapply to new positions.
    let columns = [
      { label: '', name: '', text: this.$t('columns.checkBoxColumn'), value: '1' },
      { label: this.$t('columns.orderInfo'), text: this.$t('columns.orderInfo'), value: '2', sortable: true, searchable: true, name: 'theOrderID' },
      { label: this.$t('columns.customer'), text: this.$t('columns.customer'), value: '3', sortable: true, searchable: true, name: 'full_name' },
      { label: this.$t('columns.itemDetails'), text: this.$t('columns.itemDetails'), value: '4', sortable: true, searchable: true, name: 'product_name' },
      { label: this.$t('columns.product'), text: this.$t('columns.product'), value: '5', sortable: false, searchable: false, name: 'image' },
      { label: this.$t('columns.statusLocation'), text: this.$t('columns.statusLocation'), value: '6', sortable: true, searchable: true, name: 'status' },
      { label: this.$t('columns.pricing'), text: this.$t('columns.pricing'), value: '7', sortable: false, searchable: false, name: 'price_all' },
      { label: this.$t('columns.datePayment'), text: this.$t('columns.datePayment'), value: '8', sortable: true, searchable: true, name: 'process_date' },
      { label: this.$t('columns.actions'), text: this.$t('columns.actions'), value: '9', sortable: false, searchable: false, name: 'actions' }
    ]
    columns.forEach((column) => {
      sortOrders[column.name] = -1
    })

    return {
      isMobile: false,
      loadSingleApprove: false,
      approvingDialog: false,
      approveValue: '1',
      loading: false,
      url: 'api/processed_order',
      orderDetailUrl: 'api/show_order_detail',
      approvingOrderUrl: 'api/approving_order_by_admin',
      cancelingOrderUrl: 'api/canceling_order_by_admin',
      rowIndex: '',
      orderData: {},
      alertValue: false,
      checkAlert: false,
      filterMade: false,
      orderIds: [],
      reasons: [],
      totalResult: 0,
      resultsVersion: 0,
      queryParameter: true,
      cancelData: {
        cancel_reason: ''
      },
      logDetailUrl: 'api/credit_log_transactions',
      companyUrl: 'api/company_info',
      detialData: {},
      exRate: 1,
      loadApproveAll: false,
      companyData: {
        company_name: '',
        company_address: '',
        company_email: ''
      },
      theCancelOrderID: '',
      cancelOrderModal: false,
      cancelAlert: false,
      creditLogResponse: null,
      // Process Order (single instance pattern, mirrors approvedOrder.vue/purchasedOrder.vue)
      processOrderUrl: 'api/orders',
      getItemTypeUrl: 'api/get-item-type',
      currentProcessOrder: null,
      theQuantity: { quantity: '' },
      itemTypeResult: [],
      itemTypes: [],
      updateData: {
        item_price: null,
        the_tl_price: null,
        the_aed_price: null,
        the_cny_price: null,
        the_gbp_price: null,
        the_eur_price: null,
        tax: null,
        national_tax: null,
        item_weight: null,
        internal_shipping_handling: null,
        delivery_price: null,
        kreenex_fee: null,
        quantity_charge: null,
        insurance: null,
        total_amount: null,
        from_date: null,
        to_date: null,
        description: '',
        tmp_shipping: null,
        item_weight_lb: null,
        price_weight: null,
        send_sms: false,
        discount: null,
        item_type: '',
        transportation_type: null,
        is_repackage: false,
        is_hold: false,
        other_service: '',
        local_delivery: 0,
        process_note: '',
        customer_process_note: '',
        repackage_price: null,
        hold_price: null,
        other_service_price: null,
        customer_id_fk: null,
        product_name: null,
        product_url: null,
        quantity: null,
        color: null,
        size: null,
        note: null,
        brand: null
      },
      btnsave: true,
      prevSearchQuery: '',
      apiResults: [],
      detailResults: [],
      columns: columns,
      // Grouped cells can't be hidden field-by-field any more -- same tradeoff allOrder.vue made.
      defaultHideCol: [],
      // Hidden by default (per-page preference, same localStorage convention as allOrder.vue).
      showFilters: localStorage.getItem('filtersVisible_processedOrder') === 'true',
      sortKey: 'order_id',
      sortOrders: sortOrders,
      warehouses: [],
      lightboxImage: null,
      lightboxOpen: false,
      tableData: {
        per_page: 10,
        search: '',
        column: 'updated_at',
        sortDir: 'desc',
        searchOneCol: '',
        searchOneVal: '',
        from_date: '',
        to_date: '',
        branches_id: '',
        warehouse_id_fk: ''
      },
      pagination: {
        lastPage: 1,
        currentPage: 1,
        total: 1,
        lastPageUrl: '',
        nextPageUrl: '',
        prevPageUrl: '',
        from: '',
        to: ''
      }
    }
  },

  computed: {
    sumOfSelectedOrders() {
      let sum = 0
      this.apiResults.filter(order => this.orderIds.includes(order.order_id)).forEach(x => {
        const unit = Number(x.item_price || 0) + Number(x.internal_shipping_handling || 0)
        sum += unit * Number(x.quantity || 1) + Number(x.national_tax || 0) + Number(x.delivery_price || 0)
      })

      return sum
    },

    sumOfSelectedOrdersTotalCost() {
      let sum = 0
      this.apiResults.filter(order => this.orderIds.includes(order.order_id)).forEach(x => {
        sum += x.total_amount
      })

      return sum
    },

    summaryTotalOrders() {
      if (this.orderIds.length > 0) return this.orderIds.length
      return this.filterMade
        ? this.apiResults.length
        : (this.pagination.total || this.apiResults.length)
    },
    summaryTotalAmount() {
      return this.apiResults.reduce((sum, r) => sum + (Number(r.total_amount) || 0), 0)
    },
    // Best-effort only: counts within the currently loaded page of apiResults, not a true
    // system-wide count -- hence the "(current page)" note shown alongside it in the stat card.
    ordersTodayCount() {
      const today = new Date().toISOString().split('T')[0]
      return this.apiResults.filter(r => {
        const d = r.process_date || r.order_date || r.created_at
        return d && d.substr(0, 10) === today
      }).length
    },
    // Same current-page-only caveat as ordersTodayCount above.
    pendingPaymentsCount() {
      return this.apiResults.filter(r => r.payment_status && r.payment_status !== 'paid').length
    }
  },

  methods: {
    toggleFiltersVisible() {
      this.showFilters = !this.showFilters
      localStorage.setItem('filtersVisible_processedOrder', this.showFilters ? 'true' : 'false')
    },

    // Replicates preDatatableComponent.vue's own built-in Reload button (now relocated
    // into the panel-heading here) -- reaches into the child's own filter-chip/date-range
    // reset state via $refs since that state lives inside the shared component, not here.
    reloadTable() {
      this.resetGetResults()
      const preDatatable = this.$refs.preDatatable
      if (preDatatable) {
        preDatatable.filterApplied = false
        preDatatable.resetDatePickerDefaults()
        preDatatable.ware = 'Select'
      }
    },

    paymentStatusColor(status, editCount) {
      if (status === 'not_paid' && !Number(editCount)) {
        return { bg: '#E0E0E0', color: '#616161' }
      }
      const map = {
        not_paid: { bg: '#F63049', color: '#FFFFFF' },
        partial_paid: { bg: '#FFC85C', color: '#6B4C00' },
        paid: { bg: '#89D7B7', color: '#1A5940' },
        cancel: { bg: '#FB8C00', color: '#FFFFFF' }
      }
      return map[status] || { bg: '#F5F5F5', color: '#616161' }
    },

    paymentStatusLabel(status) {
      if (!status) return ''
      return status.replace(/_/g, ' ')
    },

    /** Item price + int. shipping per unit (USD), for display when Shipping column is hidden. */
    formatPriceInclIntShipping(r) {
      const p = Number(r.item_price) || 0
      const s = Number(r.internal_shipping_handling) || 0
      const n = p + s
      return n ? '$' + n.toFixed(2) : '—'
    },

    branchEdit(branches) {
      this.tableData.branches_id = branches.toString()
      this.getResults()
    },

    showHideColumn(prevColVal, currentColVal) {
      let a = []
      let diff = []

      if (currentColVal.length < prevColVal.length) {
        for (let i = 0; i < prevColVal.length; i++) {
          a[prevColVal[i]] = true
        }

        for (let i = 0; i < currentColVal.length; i++) {
          if (a[currentColVal[i]]) {
            delete a[currentColVal[i]]
          } else {
            a[currentColVal[i]] = true
          }
        }

        for (let k in a) {
          diff.push(k)
        }

        for (let c = 0; c < diff.length; c++) {
          $('td:nth-child(' + diff[c] + '),th:nth-child(' + diff[c] + ')').toggle()
        }
      } else {
        for (let i = 0; i < currentColVal.length; i++) {
          let visible = $(
            'td:nth-child(' + currentColVal[i] + '),th:nth-child(' + currentColVal[i] + ')'
          ).is(':visible')
          if (visible) {
            $(
              'td:nth-child(' + currentColVal[i] + '),th:nth-child(' + currentColVal[i] + ')'
            ).toggle()
          }
        }
      }
    },

    getCompanyData(token) {
      axios
        .get(this.companyUrl, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.companyData = response.data.data
        })
    },

    initInvoice(modal, id) {
      let token = localStorage.getItem('access_token')
      this.getCompanyData(token)

      axios.get('api/currency-rate/latest', { headers: { Authorization: 'Bearer ' + token } })
        .then((res) => { this.exRate = res.data ? res.data.ex_rate || 1 : 1 })
        .catch(() => {})

      axios
        .get(this.logDetailUrl + '/' + id, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          this.detailResults = data.data

          this.detialData = this.detailResults[0]
        })
      this.modal(modal)
    },

    returnOrderToNewOrder(orderId) {
      let token = localStorage.getItem('access_token')
      axios
        .get(`api/return_order_to_new_order/${orderId}`, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then(() => {
          this.getResults()
          swal({
            title: this.$t('common.done'),
            text: this.$t('orders.ordersReturnedToNewOrderSection'),
            icon: 'success',
            timer: 1500,
            buttons: false
          })
        })
    },

    openCancelingOrderModal(orderId) {
      this.cancelOrderModal = true
      this.theCancelOrderID = orderId
    },

    closeCancelingOrderModal() {
      this.cancelOrderModal = false
      this.cancelAlert = false
      this.cancelData.cancel_reason = ''
    },

    cancelingOrder() {
      let token = localStorage.getItem('access_token')
      axios
        .put(this.cancelingOrderUrl + '/' + this.theCancelOrderID, this.cancelData, {
          headers: { Authorization: 'Bearer ' + token }
        })
        .then((response) => {
          this.getResults()
          this.cancelAlert = true
          let self = this
          setTimeout(() => {
            self.closeCancelingOrderModal()
          }, 1500)
        })
    },

    getReasons() {
      let token = localStorage.getItem('access_token')
      axios
        .get('api/reason/list', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let result = response.data
          this.reasons = result.map(r => ({
            text: r.cancel_reason,
            value: r.cancel_reason,
            ar: r.cancel_reason_ar,
            ku: r.cancel_reason_ku
          }))
        })
    },

    getWarehouses() {
      let token = localStorage.getItem('access_token')
      axios
        .get('api/warehouses', { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.warehouses = []
          let result = response.data.data
          result.forEach((r) => {
            this.warehouses.push({ text: r.country, value: r.warehouse_id, transportation_types: r.transportation_types, currency: r.currency })
          })
        })
    },

    getItemType() {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.getItemTypeUrl, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          this.itemTypeResult = response.data.data
          this.itemTypeResult.forEach((c) => {
            this.itemTypes.push({
              text: c.type_name,
              value: c.type_name,
              shippingPrice: c.type_int_shipping_price
            })
          })
        })
    },

    // Process Order modal (single instance pattern, mirrors approvedOrder.vue/purchasedOrder.vue)
    openProcessOrderModal(r) {
      const warehouseObj = this.warehouses.find(w => w.value === r.warehouse_id_fk)
      const enriched = Object.assign({}, r, {
        warehouse: {
          transportation_types: warehouseObj ? warehouseObj.transportation_types : []
        }
      })
      this.currentProcessOrder = enriched
      this.checkAlert = false
      this.clearProcessOrderData()
      this.theQuantity.quantity = r.quantity
      this.updateData.quantity_charge = r.quantity_charge
      this.$nextTick(() => {
        if (this.$refs.processComp) {
          this.$refs.processComp.dialog = true
          this.$refs.processComp.calculateTotalAmount()
          this.$refs.processComp.getExchangeRate()
          this.$refs.processComp.getAEDRate()
          this.$refs.processComp.getCNYRate()
          this.$refs.processComp.getGBPRate()
          this.$refs.processComp.getEURRate()
        }
      })
    },

    calculateTotalAmount() {
      let itemPrice = Number(this.updateData.item_price) || 0
      let shipping = Number(this.updateData.internal_shipping_handling) || 0
      let tax = Number(this.updateData.tax) || 0
      let nationalTax = Number(this.updateData.national_tax) || 0
      let delivery = Number(this.updateData.delivery_price) || 0
      let fee = Number(this.updateData.kreenex_fee) || 0
      let insurance = Number(this.updateData.insurance) || 0
      let qttCharge = Number(this.updateData.quantity_charge) || 0
      let discount = Number(this.updateData.discount) || 0
      let qtt = Number(this.theQuantity.quantity) || 0
      let repackagePrice = Number(this.updateData.repackage_price) || 0
      let holdPrice = Number(this.updateData.hold_price) || 0
      let otherServicePrice = Number(this.updateData.other_service_price) || 0
      const _userData = JSON.parse(localStorage.getItem('user_data') || '{}')
      const _exRate = Number(_userData.ex_rate) || 1
      let localDelivery = (Number(this.updateData.local_delivery) || 0) / _exRate

      let total = (itemPrice + shipping) * qtt + tax + nationalTax + delivery + fee + insurance + qttCharge + localDelivery

      if (this.updateData.is_repackage) total += repackagePrice
      if (this.updateData.is_hold) total += holdPrice
      if (otherServicePrice > 0) total += otherServicePrice
      if (discount > 0) total -= discount

      this.updateData.total_amount = total.toFixed(2)
    },

    clearProcessOrderData() {
      this.updateData.item_price = null
      this.updateData.the_tl_price = null
      this.updateData.the_aed_price = null
      this.updateData.the_cny_price = null
      this.updateData.the_gbp_price = null
      this.updateData.the_eur_price = null
      this.updateData.tax = null
      this.updateData.item_weight = null
      this.updateData.internal_shipping_handling = 0
      this.updateData.delivery_price = null
      this.updateData.kreenex_fee = null
      this.updateData.insurance = null
      this.updateData.total_amount = null
      this.updateData.from_date = null
      this.updateData.to_date = null
      this.updateData.quantity_charge = null
      this.updateData.description = ''
      this.updateData.item_type = ''
      this.updateData.item_weight_lb = null
      this.updateData.discount = null
      this.updateData.send_sms = false
      this.updateData.is_repackage = false
      this.updateData.is_hold = false
      this.updateData.other_service = ''
      this.updateData.repackage_price = null
      this.updateData.hold_price = null
      this.updateData.other_service_price = null
      this.updateData.local_delivery = null
      this.updateData.national_tax = null
      this.updateData.process_note = ''
      this.updateData.customer_process_note = ''
      this.updateData.customer_id_fk = null
      this.updateData.product_name = null
      this.updateData.product_url = null
      this.updateData.quantity = null
      this.updateData.color = null
      this.updateData.size = null
      this.updateData.note = null
      this.updateData.brand = null
      this.updateData.order_image = null
      this.checkAlert = false
    },

    processOrder(event, id, stime, stime1, stime2) {
      let token = localStorage.getItem('access_token')
      this.loading = true
      this.updateData.send_sms = this.updateData.send_sms.toString()

      const formData = new FormData()

      Object.keys(this.updateData).forEach(key => {
        if (key === 'customer_process_note_image' || key === 'order_image') return
        if (this.updateData[key] !== null && this.updateData[key] !== undefined) {
          formData.append(key, this.updateData[key])
        }
      })

      if (this.updateData.customer_process_note_image instanceof File) {
        formData.append('customer_process_note_image', this.updateData.customer_process_note_image)
      }

      if (this.updateData.order_image instanceof File) {
        formData.append('order_image', this.updateData.order_image)
      }

      axios.post(this.processOrderUrl + '/' + id, formData, {
        headers: { Authorization: 'Bearer ' + token }
      })
        .then((response) => {
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
          this.checkAlert = true
          this.getResults()
          const message = response.data.message
          if (message && typeof message === 'object' && message.adjustment_note) {
            swal(this.$t('orders.orderUpdatedTitle'), message.adjustment_note, 'success')
          }
          let b = this
          setTimeout(function () {
            b.checkAlert = false
          }, 1500)
        })
        .catch((error) => {
          const rawMsg = error && error.response && error.response.data && error.response.data.message
          const msg = rawMsg && typeof rawMsg === 'object'
            ? Object.values(rawMsg).flat().join('\n')
            : (rawMsg || this.$t('orders.errorOccurredProcessingOrder'))
          swal(this.$t('common.error'), msg, 'error')
          this.loading = false
          this.clearProcessOrderData()
          stime.time = ''
          stime1.time = ''
          stime2.time = ''
        })
    },

    handleWarehouseFilter(val) {
      this.tableData.warehouse_id_fk = Array.isArray(val) && val.length ? val.join(',') : ''
      this.url = 'api/processed_order'
      this.getResults()
    },

    getResults(url = this.url) {
      var token = localStorage.getItem('access_token')
      let theDatatable = document.querySelector('.the-datatable')
      let thePanel = document.querySelector('.panel-flat')

      if (theDatatable != null) {
        theDatatable.classList.add('slide-table-out')
        thePanel.style.overflowX = 'auto'
      }

      axios
        .get(url, { params: this.tableData, headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          const isFlatArray = Array.isArray(data.data)
          if (isFlatArray) {
            this.filterMade = true
            this.apiResults = data.data
          } else {
            this.filterMade = false
            this.apiResults = data.data.data
          }
          this.resultsVersion++

          if (this.apiResults.length === 0) {
            this.tableData.searchOneCol = ''
            this.tableData.searchOneVal = ''
            this.tableData.search = ''
            this.alertValue = true
          } else {
            this.alertValue = false
          }

          if (isFlatArray) {
            this.removeConfigPagination()
          } else {
            this.configPagination(data.data)
          }
          theDatatable.classList.remove('slide-table-out')
          setTimeout(function () {
            thePanel.style.overflowX = 'auto'
          }, 0)
        })
        .then(() => {
          this.totalResult = 0
          for (let i = 0; i < this.apiResults.length; i++) {
            this.totalResult += 1
          }
        })
        .catch((errors) => { })
    },

    removeConfigPagination() {
      this.pagination.lastPage = ''
      this.pagination.currentPage = ''
      this.pagination.total = ''
      this.pagination.lastPageUrl = ''
      this.pagination.nextPageUrl = ''
      this.pagination.prevPageUrl = ''
      this.pagination.from = ''
      this.pagination.to = ''
    },

    resetGetResults() {
      this.queryParameter = false
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      this.tableData.search = ''
      this.sortKey = 'updated_at'
      this.tableData.sortDir = 'desc'
      this.tableData.column = 'updated_at'
      this.tableData.from_date = ''
      this.tableData.to_date = ''
      this.tableData.warehouse_id_fk = ''
      this.getResults()
    },

    perPageFix(perPage) {
      this.tableData.per_page = perPage
      this.getResults()
    },

    changePage(pageUrl) {
      this.url = pageUrl
      this.getResults()
    },

    modal(modal) {
      $('#' + modal).modal({ backdrop: 'static' })
    },

    closeModal(modal) {
      let theModal = $('#' + modal)
      theModal.modal('toggle')
    },

    initDetail(modal, id) {
      let token = localStorage.getItem('access_token')
      axios
        .get(this.orderDetailUrl + '/' + id, { headers: { Authorization: 'Bearer ' + token } })
        .then((response) => {
          let data = response.data
          this.detailResults = data.data
          this.detialData = this.detailResults[0]
        })
      this.modal(modal)
    },

    checkAll() {
      this.orderIds = this.apiResults.map(r => r.order_id)
    },

    unCheckAll() {
      this.orderIds = []
    },

    approvingMultipleOrder() {
      if (this.orderIds.length === 0) {
        swal(this.$t('common.error'), this.$t('orders.pleaseCheckOrderToApprove'), 'error')
        return
      }

      this.loadApproveAll = true

      swal({
        title: this.$t('common.areYouSure'),
        text: this.$t('orders.onceConfirmedAllApproved'),
        icon: 'warning',
        buttons: true
      })
        .then((approved) => {
          if (approved) {
            let len = this.orderIds.length
            let ids = ''
            for (let i = 0; i < len; i++) {
              ids += String(this.orderIds[i])
              if (i !== len - 1) {
                ids += ','
              }
            }
            let token = localStorage.getItem('access_token')
            axios
              .get('api/approving_multiple_order', {
                headers: { Authorization: 'Bearer ' + token },
                params: { ids: ids }
              })
              .then((response) => {
                this.getResults()
                swal({
                  title: this.$t('common.done'),
                  text: '',
                  icon: 'success',
                  timer: 1500,
                  buttons: false
                })
                this.unCheckAll()
                this.orderIds = []
                this.loadApproveAll = false
              })
              .catch((error) => {
                this.loadApproveAll = false
                swal(this.$t('common.error'), error.response.data.message, 'error')
              })
          } else {
            this.loadApproveAll = false
            this.getResults()
          }
        })
        .catch(() => {
          this.loadApproveAll = false
          this.getResults()
        })
    },

    approveOrder() {
      this.loadSingleApprove = true
      let token = localStorage.getItem('access_token')
      axios.post(this.approvingOrderUrl, this.orderData, {
        headers: { Authorization: 'Bearer ' + token }
      }).then((response) => {
        this.loadSingleApprove = false
        this.creditLogResponse = response.data.message
        this.getResults()
        this.approvingDialog = false
        this.orderData = {}
        swal({
          title: '',
          text: this.$t('orders.viewOrderInvoiceConfirm'),
          icon: 'warning',
          buttons: true
        }).then((confirm) => {
          if (confirm) {
            this.initInvoice('modal_showAppLog', this.creditLogResponse[0].credit_log_transaction_id)
          }
        })
      })
        .catch((error) => {
          this.loadSingleApprove = false
          if (typeof error.response.data.message === 'string') {
            swal(this.$t('common.error'), error.response.data.message, 'error')
          } else {
            let errMsg = ''
            let index = 1
            for (let x in error.response.data.message) {
              errMsg += index + '- ' + error.response.data.message[x] + ' '
              index += 1
            }

            swal(this.$t('common.error'), errMsg, 'error')
          }
        })
    },

    approvingOrder(data) {
      this.orderData = data
      this.approvingDialog = true
    },

    searchMe(str) {
      this.tableData.searchOneCol = ''
      this.tableData.searchOneVal = ''
      if (str.trim() !== '') {
        this.tableData.search = str
        this.prevSearchQuery = str
        this.getResults()
      } else {
        swal({
          text: this.$t('common.typeToSearch'),
          icon: 'warning',
          dangerMode: true
        })
        if (this.prevSearchQuery !== '') {
          this.tableData.search = ''
          this.prevSearchQuery = ''
          this.getResults()
        }
      }
    },

    sortByColumn(str) {
      this.tableData.column = str
      this.tableData.sortKey = str
      this.tableData.sortDir = this.tableData.sortDir === 'desc' ? 'asc' : 'desc'
      this.getResults()
    },

    searchOneCol(columnName, str) {
      this.tableData.searchOneCol = columnName
      this.tableData.searchOneVal = str
      this.getResults()
    },

    filterTable(fromDate, toDate) {
      this.tableData.from_date = fromDate
      this.tableData.to_date = toDate
      this.getResults()
    },

    resetGetResults2() {
      this.resetGetResults()
    },

    configPagination(data) {
      this.pagination.lastPage = data.last_page
      this.pagination.currentPage = data.current_page
      this.pagination.total = data.total
      this.pagination.lastPageUrl = data.last_page_url
      this.pagination.nextPageUrl = data.next_page_url
      this.pagination.prevPageUrl = data.prev_page_url
      this.pagination.from = data.from
      this.pagination.to = data.to
    }
  }
}
</script>

<style>
/* Mobile order cards: see assets/mobile-order-cards.css */

@media screen and (max-width: 600px) {
  th {
    font-weight: bold;
    color: #273246;
    padding-left: 5px !important;
  }

  td {
    padding-left: 5px !important;
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
}

.table > thead > tr > th, .table > tbody > tr > th, .table > tfoot > tr > th, .table > thead > tr > td, .table > tbody > tr > td, .table > tfoot > tr > td
{
  padding: 0 5px !important;
}

.panel-heading h4 {
  text-transform: uppercase;
  margin-left: -20px;
  padding-left: 10px;
  font-weight: 500;
}

.icon-enlarge,
.icon-pencil {
  color: navy;
}

.icon-bin2 {
  color: red;
}

.icon-calendar {
  color: green;
}

.btn {
  transition: all 0.5s;
}

.col-xs-12 {
  padding-top: 10px;
  padding-bottom: 10px;
}
.overlay--active:before {
  opacity: 50 !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.dialog {
  box-shadow: 0 3px 5px -1px rgba(14, 14, 14, 0.212) !important;
  overflow: hidden;
  overflow-y: hidden;
}
.page-stats {
  align-items: center;
  position: relative;
  top: -8px;
  margin-right: 5px;
}

.has-error .select2-selection--single:not([class*='bg-']):not([class*='border-']) {
  border-bottom-color: red;
}

@media screen and (max-width: 767px) {
  .form-group {
    margin-top: 0;
    margin-bottom: 0;
  }
}

a:hover {
  color: #03a9f4;
  position: relative;
}
a[tooltip]:hover:after {
  content: attr(tooltip);
  padding: 8px 12px;
  color: #ffffff;
  position: absolute;
  right: 80%;
  bottom: 150%;
  white-space: nowrap;
  z-index: 9999;
  -moz-border-radius: 3px;
  -webkit-border-radius: 3px;
  border-radius: 3px;
  -moz-box-shadow: 0px 0px 2px #484b4e;
  -webkit-box-shadow: 0px 0px 2px #484b4e;
  box-shadow: 0px 0px 2px #484b4e;
  background-color: #484b4e;
}

.customer-name-stack {
  display: inline-flex;
  flex-direction: column;
  vertical-align: middle;
}
.customer-phone-line {
  display: block;
  font-size: 14px;
  color: #666;
}
.combined-cell-td {
  vertical-align: middle;
}
.combined-cell {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 3px;
  font-size: 15px;
  line-height: 1.4;
  white-space: nowrap;
}
.combined-cell-label {
  color: #888;
}
</style>



// WEBPACK FOOTER //
// src/components/processedOrder.vue